import { useState } from "react";
import { motion } from "framer-motion";
import { useRubro } from "./rubro";
import { Reveal } from "./Reveal";

// ---------------------------------------------------------------------------
// FLOW — interactive "Qué pasa cuando te escribe un cliente" walkthrough.
// Left: clickable steps. Right: detail panel with a chat mock.
// ---------------------------------------------------------------------------

type ChatLine = {
  from: string;
  align: "flex-start" | "flex-end";
  bg: string;
  fg: string;
  text: string;
};

type FlowStep = {
  n: string;
  title: string;
  short: string;
  who: string; // short tag
  whoLong: string;
  whoColor: string;
  numBg: string;
  numFg: string;
  border: string;
  bg: string;
  body: string;
  chat: ChatLine[];
};

function buildFlow(rb: { label: string; biz: string }): FlowStep[] {
  return [
    {
      n: "1",
      title: "Te encuentra en Google",
      short: "Web + ficha de Google optimizada",
      who: "SISTEMA",
      whoLong: "Lo hace el sistema",
      whoColor: "#A78BFA",
      numBg: "rgba(139,92,246,0.16)",
      numFg: "#A78BFA",
      border: "#221F2A",
      bg: "#0F0D14",
      body: `Alguien busca "${rb.label} cerca de mí" y te encuentra a vos, no a la competencia. Tu web y tu ficha de Google ya están optimizadas para eso.`,
      chat: [
        {
          from: "Google",
          align: "flex-start",
          bg: "#1D1A26",
          fg: "#D8D5E0",
          text: `"${rb.label} cerca de mí"`,
        },
        {
          from: "Resultado",
          align: "flex-end",
          bg: "#2A1F4A",
          fg: "#E9E1FF",
          text: `${rb.biz} · ★ 4,9 (212)`,
        },
      ],
    },
    {
      n: "2",
      title: "Te escribe por WhatsApp",
      short: "WhatsApp, Instagram y Facebook a una bandeja",
      who: "CLIENTE",
      whoLong: "Lo hace el cliente",
      whoColor: "#9C98A6",
      numBg: "#1D1A26",
      numFg: "#BAB6C4",
      border: "#221F2A",
      bg: "#0F0D14",
      body: "La consulta entra a las 22:40 del domingo. No espera a que abras el celular el lunes: el sistema la recibe al toque.",
      chat: [
        {
          from: "Cliente · WhatsApp",
          align: "flex-start",
          bg: "#1D1A26",
          fg: "#D8D5E0",
          text: "Hola, quería un presupuesto 👋",
        },
      ],
    },
    {
      n: "3",
      title: "El Agente IA responde y califica",
      short: "Responde como vos, a cualquier hora",
      who: "AGENTE IA",
      whoLong: "Lo hace el Agente IA",
      whoColor: "#A78BFA",
      numBg: "rgba(139,92,246,0.16)",
      numFg: "#A78BFA",
      border: "#221F2A",
      bg: "#0F0D14",
      body: "El Agente IA contesta al instante, hace las preguntas necesarias para entender si es un cliente real y agenda el trabajo o la llamada.",
      chat: [
        {
          from: "Agente IA",
          align: "flex-end",
          bg: "#2A1F4A",
          fg: "#E9E1FF",
          text: "¡Hola! Gracias por escribir. ¿Para qué zona sería?",
        },
        {
          from: "Cliente",
          align: "flex-start",
          bg: "#1D1A26",
          fg: "#D8D5E0",
          text: "Para Palermo 👍",
        },
      ],
    },
    {
      n: "4",
      title: "Te deriva el cierre",
      short: "Cuando está listo, te lo pasa a vos",
      who: "VOS",
      whoLong: "Intervenís vos",
      whoColor: "#34D399",
      numBg: "rgba(52,211,153,0.16)",
      numFg: "#34D399",
      border: "#221F2A",
      bg: "#0F0D14",
      body: "El Agente no cierra por vos. Cuando el contacto está listo, te lo deriva a vos o a tu vendedor. El último paso lo da una persona.",
      chat: [
        {
          from: "Agente IA → Vos",
          align: "flex-end",
          bg: "#2A1F4A",
          fg: "#E9E1FF",
          text: "Cliente listo para cerrar. ¿Le pasás el presupuesto?",
        },
      ],
    },
    {
      n: "5",
      title: "Seguimiento + reseña + reactivación",
      short: "El sistema le sigue el rastro y vuelve a alimentarse",
      who: "SISTEMA",
      whoLong: "Lo hace el sistema",
      whoColor: "#A78BFA",
      numBg: "rgba(139,92,246,0.16)",
      numFg: "#A78BFA",
      border: "#221F2A",
      bg: "#0F0D14",
      body: "Marcás el presupuesto como enviado y el sistema hace el seguimiento solo. Después del trabajo, pide la reseña. Y a los 12 meses, reactiva al cliente. Vuelve a empezar.",
      chat: [
        {
          from: "Sistema",
          align: "flex-start",
          bg: "#1D1A26",
          fg: "#D8D5E0",
          text: "Seguimiento automático · día 2, 5, 10 y 20",
        },
        {
          from: "Sistema",
          align: "flex-start",
          bg: "#1D1A26",
          fg: "#D8D5E0",
          text: "Reseña pedida ✓ · cliente reactivado ↺",
        },
      ],
    },
  ];
}

export function HomeFlow() {
  const { rb } = useRubro();
  const flow = buildFlow(rb);
  const [cur, setCur] = useState(0);
  const step = flow[cur];

  return (
    <section
      id="flujo"
      data-screen-label="Recorrido"
      className="bg-[#0F0D14] border-t border-b border-[#221F2A] scroll-mt-16"
    >
      <div
        className="mx-auto px-6"
        style={{
          maxWidth: 1240,
          paddingTop: "clamp(80px,10vw,128px)",
          paddingBottom: "clamp(80px,10vw,128px)",
        }}
      >
        <Reveal>
          <div
            className="font-mono text-[13px] text-[#A78BFA]"
            style={{ letterSpacing: "0.08em" }}
          >
            02 — CÓMO FUNCIONA
          </div>
          <h2
            className="mt-5 text-[#EEEDF2] font-medium"
            style={{
              fontSize: "clamp(36px,4.8vw,64px)",
              lineHeight: 1.02,
              letterSpacing: "-0.025em",
              maxWidth: 860,
              textWrap: "balance" as never,
            }}
          >
            Qué pasa cuando te escribe un cliente.
          </h2>
          <p className="mt-4 max-w-[560px] leading-[1.55] text-[17px] text-[#9C98A6]">
            Sin cajas negras. Tocá cada paso para ver qué hace el sistema y en
            qué momento intervenís vos.
          </p>
          <div
            className="inline-flex items-center gap-2 mt-5 rounded-full px-3 py-1.5 text-[13px] text-[#D3CCE6]"
            style={{
              background: "rgba(139,92,246,0.10)",
              border: "1px solid #3A2D63",
            }}
          >
            Ejemplo para:{" "}
            <span className="font-semibold text-[#EEEDF2]">{rb.label}</span>
          </div>
        </Reveal>

        <div
          className="mt-14 grid gap-6 items-stretch"
          style={{
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,380px),1fr))",
          }}
        >
          {/* Steps list */}
          <div className="flex flex-col gap-2">
            {flow.map((f, i) => {
              const active = i === cur;
              return (
                <button
                  key={f.n}
                  onClick={() => setCur(i)}
                  className="grid items-center rounded-xl border text-left transition-colors hover:border-[#433C52]"
                  style={{
                    gridTemplateColumns: "44px 1fr auto",
                    gap: 14,
                    padding: "18px 20px",
                    borderColor: active ? "#433C52" : f.border,
                    background: active ? "#17141F" : f.bg,
                  }}
                >
                  <div
                    className="rounded-full grid place-items-center font-mono text-[13px]"
                    style={{
                      width: 44,
                      height: 44,
                      background: f.numBg,
                      color: f.numFg,
                    }}
                  >
                    {f.n}
                  </div>
                  <div>
                    <div className="font-semibold text-[16.5px] text-[#EEEDF2]">
                      {f.title}
                    </div>
                    <div className="text-[13.5px] text-[#9C98A6] mt-0.5">
                      {f.short}
                    </div>
                  </div>
                  <span
                    className="font-mono text-[11.5px]"
                    style={{ color: f.whoColor, letterSpacing: "0.05em" }}
                  >
                    {f.who}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div
            className="bg-[#0A090D] border border-[#2A2633] rounded-2xl p-7 flex flex-col gap-[18px]"
            style={{ minHeight: 420 }}
          >
            <div className="flex justify-between items-center gap-3 flex-wrap">
              <span className="font-mono text-[12px] text-[#9C98A6]">
                PASO {step.n} / 5
              </span>
              <span
                className="text-[12px] rounded-full px-2.5 py-1"
                style={{ background: step.numBg, color: step.numFg }}
              >
                {step.whoLong}
              </span>
            </div>
            <h3 className="text-[26px] font-semibold tracking-[-0.02em] text-[#EEEDF2]">
              {step.title}
            </h3>
            <p className="text-[#BAB6C4] leading-[1.6]">{step.body}</p>
            <div className="mt-auto flex flex-col gap-2.5 p-[18px] bg-[#13111A] border border-[#221F2A] rounded-xl">
              {step.chat.map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.08 }}
                  className="flex flex-col"
                  style={{ alignItems: c.align, gap: 4 }}
                >
                  <span className="font-mono text-[11px] text-[#6E6A7A]">
                    {c.from}
                  </span>
                  <div
                    className="max-w-[85%] px-3.5 py-2.5 rounded-xl text-[14px] leading-[1.45]"
                    style={{ background: c.bg, color: c.fg }}
                  >
                    {c.text}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="flex justify-between gap-3">
              <button
                onClick={() => setCur((c) => Math.max(0, c - 1))}
                className="text-[14px] text-[#9C98A6] hover:text-[#EEEDF2] transition-colors py-2"
              >
                ← Anterior
              </button>
              <button
                onClick={() => setCur((c) => Math.min(flow.length - 1, c + 1))}
                className="text-[14px] text-[#A78BFA] font-medium hover:text-[#C4B5FD] transition-colors py-2"
              >
                Siguiente paso →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
